import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Divider,
  Stack
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import CustomerLogApi from 'api/faber/CustomerLogAPI'
import DataApi from 'api/faber/DataApi'
import SubscriptionsAPI from 'api/faber/SubscriptionsAPI'
import AbstractAuditLogDataGrid from 'common/AbstractAuditLogDataGrid'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import {
  getWinbackFilterOptions,
  IFilterField
} from 'common/AbstractDataGridToolbar/CustomWinbackDataGridFilter'
import { CustomWinbackToolbar } from 'common/AbstractDataGridToolbar/CustomWinbackToolbar'
import ColorBox from 'common/Box/ColorWhiteBox'
import FaberFeatureToggle from 'common/FaberComponents/FaberFeatureToggle'
import ProfileListInfo from 'common/FaberComponents/Layout/FaberColumnLayout'
import TextColorFontHeader from 'common/Typography/TextColorFontHeader'
import circlesOrbitColorPalette from 'common/ui/circlesColorTheme'
import format from 'date-fns/format'
import { mapStatesToFrontendDropdown } from 'helpers/backendStatesMapping'
import {
  STATUS_UPDATE,
  WINBACK_LOG
} from 'helpers/column-generator/generateColumnHeader'
import { generateColumnHeader } from 'helpers/column-generator/generateColumnHeader'
import { changeMonth } from 'helpers/date'
import OrbitSession from 'hooks/useCookies'
import useModal from 'hooks/useModal'
import useOrbitFlag from 'hooks/useOrbitFlag'
import {
  ActionMenuForOneUser,
  EmailSentLogsV2,
  NewEsimReplacement,
  NoPermissionSnackbar,
  RefreshNetworkDialog,
  SmartDelayFormLogsV2
} from 'pages/Customers/sharedComponents'
import {
  EsimRequestDialog,
  EsimRequestV2Dialog,
  UpdateFaberUserDetailsDialog
} from 'pages/Customers/sharedDialogs'
import EsimAllocateByWhitelistIDDialog from 'pages/Customers/sharedDialogs/EsimAllocateByWhitelistID'
import {
  BillWaiverAuditLogType,
  CustomerDetailsLogType,
  VoucherAuditLogType
} from 'types/api/faber/customerAuditLog.type'
import {
  ISimServiceStatusDetails,
  SubscriptionsTableRows
} from 'types/api/faber/subscription.type'

import { CancelSubV2Dialog, WinbackDialog } from '../dialogs'

import CDRLogsV2 from './CDRLogsV2'
import DataRemainingCard from './DataRemainingCard'
import EditSubscriptionActionMenu from './EditSubscriptionActionMenu'
import EsimStatusCard from './EsimStatusCard'
import SubHistory from './SubHistory'
import SubStatusCard from './SubStatusCard'
import SuspendOrUnsuspendDialog from './SuspendOrUnsuspendDialog'

type WinbackType = BillWaiverAuditLogType | VoucherAuditLogType

function UserProfileV2({customerRow:propCustomerRow}: UserProfileProps) {
  const location = useLocation()
  const acctStatusColumns = generateColumnHeader(STATUS_UPDATE)
  const winbackColumns = generateColumnHeader(WINBACK_LOG)
  const subHistoryFlag = useOrbitFlag('subscription-history')
  const upgradeNowFlag = useOrbitFlag('CH-upgrade-now')
  const dataRemainingCardFlag = useOrbitFlag('data-remaining-card')
  const [actionEvent, setActionEvent] = useState('')


  const { id } = useParams()

  /*PARAMS*/

  const customerRow = propCustomerRow || location.state?.customer || {
    userID: id,
    firstName: '',
    lastName: '',
    userEmail: '',
    iccid: '',
    msisdn: '',
    planName: '',
    planStatus: '',
    subscriptionStartAt: '',
    cycleCount: '',
  };
  const faberToken = OrbitSession.get('faberToken')
  const faberAdminRole = OrbitSession.get('role')
  const [customerData, setCustomerData] =
    useState<SubscriptionsTableRows>(customerRow)

  /*DIALOGS EVENT HANDLER */
  const [
      isEsimAllocateByWhitelistIDDialogOpen,
      setIsEsimAllocateByWhitelistIDDialogOpen
    ] = useState(false)
  const [esimDialogOpen, setEsimDialogOpen] = useState(false)
  const [winbackDialog, setWinbackDialog] = useState(false)
  const [suspendOrUnsuspendDialogOpen, setSuspendOrUnsuspendDialogOpen] =
    useState(false)
  const [refreshNetworkDialogOpen, setRefreshNetworkDialogOpen] =
    useState(false)
  const [expanded, setExpanded] = React.useState<string | false>(false)

  const [simServiceStatus, setSimServiceStatus] =
    useState<ISimServiceStatusDetails>()

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false)
    }

  const [dataRemaining, setDataRemaining] = useState(0)

  const [cancelSubDialogOpen, setCancelSubDialogOpen] = useState(false)

  const {
    isShowing: permissionSnackbarOpen,
    openModal: handlePermissionSnackbarOpen,
    closeModal: handlePermissionSnackbarClose
  } = useModal()

  const handleEsimRequestDialogClose = () => {
    setEsimDialogOpen(false)
  }
  const handleEsimRequestDialogOpen = (event: string) => {
    setActionEvent(event)
    setEsimDialogOpen(true)
  }

  const handleEsimRequestDialogV2Open = () => {
    setEsimDialogOpen(true)
  }

  const handleRefreshNetworkDialogOpen = () => {
    setRefreshNetworkDialogOpen(true)
  }

  const handleRefreshNetworkDialogClose = () => {
    setRefreshNetworkDialogOpen(false)
  }

  const handleSuspendOrUnsuspendDialogOpen = () => {
    if (faberAdminRole !== 'opsowner') {
      handlePermissionSnackbarOpen()
    } else setSuspendOrUnsuspendDialogOpen(true)
  }

  const handleSuspendOrUnsuspendDialogClose = () => {
    setSuspendOrUnsuspendDialogOpen(false)
  }

  const handleCancelSubDialogOpen = () => {
    if (faberAdminRole === 'opsowner') {
      setCancelSubDialogOpen(true)
    } else handlePermissionSnackbarOpen()
  }
  const handleCancelSubDialogClose = () => {
    setCancelSubDialogOpen(false)
  }

  /*FOR DATA GRID */
  const [rows, setRows] = useState<CustomerDetailsLogType[]>([])
  const [page, setPage] = useState(0)
  const [voucherRows, setVoucherRows] = useState<VoucherAuditLogType[]>([])
  const [waiverRows, setWaiverRows] = useState<BillWaiverAuditLogType[]>([])
  const [winbackRows, setWinbackRows] = useState<WinbackType[]>([])

  /*FOR WINBACK LOGS DATA GRID */
  const [filterActive, setFilterActive] = useState<boolean>(false)
  const [filteredRows, setFilteredRows] = useState<WinbackType[]>([])
  const [winbackFilterFieldsChecked, setWinbackFilterFieldsChecked] = useState<
    string[]
  >([])

  // FOR IMSI PROFILE
  // const [currentImsi, setCurrentImsi] = useState('')
  // const [expectedImsi, setExpectedImsi] = useState('')
  // const [imsiTextColor, setImsiTextColor] = useState('')
  const [showRefreshNetworkButton, setShowRefreshNetworkButton] =
    useState(false)

  //FOR ESIM STATUS
  const [esimStatus, setEsimStatus] = useState('')
  const [tooltipInfo, setTooltipInfo] = useState('')
  const [esimTextColor, setEsimTextColor] = useState('')

  /*FOR DIFFERENT FORM FIELDS */
  const [endDate] = useState<Date>(changeMonth(new Date(), +3))
  const [currentBillingDate, setCurrentBillingDate] = useState('')
  const [nextBillingDate, setNextBillingDate] = useState('')
  const [nextPlan] = useState(customerRow.planName)

  const {
    data: getSubHistory,
    isLoading: getSubHistoryLoading,
    refetch: refetchSubHistory
  } = useQuery(['getSubHistory', faberToken, customerRow.userID], async () => {
    const subHistoryResponse =
      await SubscriptionsAPI.getCustomerSubscriptionHistory({
        faberToken: faberToken,
        user_id: customerRow.userID
      })
    return subHistoryResponse.data.data
  })

  /*API CALL */
  const {
    data: getAllSubscriptions,
    isLoading: gettingAllSubscriptions,
    refetch: refetchSubscriptionsReq
  } = useQuery(['getAllSubscriptions', [id, endDate]], async () => {
    return SubscriptionsAPI.getAllSubscriptions(
      faberToken,
      10,
      0,
      [],
      [],
      'Customer ID',
      id ? id : '',
      'DESC',
      '2022-10-20',
      format(endDate, 'yyyy-MM-dd')
    )
  })

  // ACCOUNT AND PLAN STATUS API CALL
  const {
    data: dataLog,
    refetch: refetchCustomerLogs,
    isLoading: isStatusUpdateLogsLoading
  } = useQuery(
    ['getUpdateLog', customerRow.userID],
    async () => {
      const customerLogsResponse = await CustomerLogApi.getCustomerLogs(
        customerRow.userID,
        faberToken
      )
      return customerLogsResponse.data
    },
    {
      enabled: expanded === 'panel2'
    }
  )
  /* VOUCHER AND WAIVER LOGS API*/
  const { refetch: refetchVoucherData, isLoading: isVoucherWaiverLogsLoading } = useQuery(
  ['getCustomerVouchers', customerRow.userID],
  async () => {
    const winbackVoucherLogs = await CustomerLogApi.getVoucherLogs(customerRow.userID, faberToken);
    return winbackVoucherLogs || { data: { data: [] } };
  },
  {
    onError: () => setVoucherRows([]),
    onSuccess: (res) => {
      setVoucherRows(Array.isArray(res.data?.data) ? res.data.data : []);
    },
    enabled: !!customerRow.userID && expanded === 'panel4',
  }
);
  const { refetch: refetchWaiverData, isLoading: isBillWaiverLogsLoading } = useQuery(
  ['getCustomersWaivers', customerRow.userID],
  async () => {
    const winbackWaiverLogs = await CustomerLogApi.getWaiverLogs(customerRow.userID, faberToken);
    return winbackWaiverLogs || { data: { data: [] } };
  },
  {
    onError: () => setWaiverRows([]),
    onSuccess: (res) => {
      setWaiverRows(Array.isArray(res.data?.data) ? res.data.data : []);
    },
    enabled: !!customerRow.userID && expanded === 'panel4',
  }
);


  //IMSI API
  // const { refetch: refetchIMSI, isLoading: isImsiLoading } = useQuery(
  //   ['getIMSIlogs', customerRow.iccid],
  //   async () => {
  //     const getImsiRecords = await DataApi.getImsiData(
  //       faberToken,
  //       customerRow.iccid
  //     )
  //     return getImsiRecords.data
  //   },
  //   {
  //     onSuccess: (res) => {
  //       if (res.data.length > 0) {
  //         if (res.data[0].expectedIMSI.includes(String(res.data[0].imsi))) {
  //           setImsiTextColor(circlesOrbitColorPalette.success.main)
  //           setCurrentImsi(String(res.data[0].imsi))
  //           setExpectedImsi(res.data[0].expectedIMSI)
  //         } else {
  //           setImsiTextColor(circlesOrbitColorPalette.error.main)
  //           setCurrentImsi(String(res.data[0].imsi))
  //           setExpectedImsi(res.data[0].expectedIMSI)
  //         }
  //       } else {
  //         setImsiTextColor(circlesOrbitColorPalette.neutrals[500])
  //         setCurrentImsi('-')
  //         setExpectedImsi('-')
  //       }
  //     }
  //   }
  // )

  //eSIM status API
  const { refetch: refetchEsimStatus, isLoading: isEsimStatusLoading } =
    useQuery(
      ['get-esim-status', customerRow.userID],
      async () => {
        const { data } = await DataApi.getEsimStatus(
          faberToken,
          customerRow.userID
        )
        return data
      },
      {
        onSuccess: (res) => {
          setEsimStatus(res.data.state)
          switch (res.data.state) {
            case 'RELEASED':
              setTooltipInfo(
                'The eSIM has been sent to the user but not downloaded into a device yet'
              )
              setEsimTextColor(circlesOrbitColorPalette.neutrals[500])

              break
            case 'DISABLED':
              setTooltipInfo(
                'The eSIM has been downloaded into a device but not turned on. Advise user to turn on the line from their phone settings'
              )
              setEsimTextColor('#ed6c02')
              break
            case 'ENABLED':
              setTooltipInfo(
                'The eSIM has been downloaded into a device and is active'
              )
              setEsimTextColor(circlesOrbitColorPalette.success.main)
              setShowRefreshNetworkButton(true)
              break
            case 'DELETED':
              setTooltipInfo(
                'The eSIM profile has been removed from the device'
              )
              setEsimTextColor(circlesOrbitColorPalette.error.main)
              break
            case 'ERROR':
              setTooltipInfo('Please perform an eSim switch the customer')
              setEsimTextColor(circlesOrbitColorPalette.error.main)
              break
          }
          return res.data
        }
      }
    )

  // simStatusCard API
  const {
    isLoading: isSimServiceStatusLoading,
    refetch: refetchSimServiceStatus
  } = useQuery(
    ['getSimServiceStatus', customerRow.userID],
    async () => {
      return SubscriptionsAPI.getSimServiceStatus({
        faberToken: faberToken,
        userID: customerRow.userID
      })
    },
    {
      onSuccess: (res) => {
        setSimServiceStatus(res.data.data)
      },
      onError: () => {
        toast.error('Error getting simServiceStatus')
      }
    }
  )

  //Data remaining card API
  const { isLoading: isDataRemainingLoading, refetch: refetchDataRemaining } =
    useQuery(
      ['getOverviewCRDData', customerRow.iccid],
      async () => {
        const getCDROverviewDataResponse = await DataApi.getDataCDROverview(
          customerRow.iccid,
          faberToken
        )
        return getCDROverviewDataResponse.data
      },
      {
        onSuccess: (res) => {
          setDataRemaining(res.totalDataAllocatedMB - res.totalDataUsedMB)
        },
        onError: () => {
          return {}
        },
        enabled: expanded === 'panel1'
      }
    )

  useEffect(() => {
    // refetchIMSI()
    refetchEsimStatus()
    refetchSimServiceStatus()
    refetchDataRemaining()
  }, [customerRow, dataLog])

  const filterFields = useMemo(() => {
    const winbackFilterFields: IFilterField[] = [
      {
        values: getWinbackFilterOptions(
          winbackRows.map((row) => row.updateStatus),
          ['VOUCHER', 'WAIVER']
        ),
        checked: winbackFilterFieldsChecked,
        setChecked: setWinbackFilterFieldsChecked,
        name: 'Winback',
        index: 'Winback'
      }
    ]
    return winbackFilterFields
  }, [voucherRows, waiverRows, winbackFilterFieldsChecked, filteredRows])

  /* FUNCTION CALLS */
  const onSuccessfulFormSubmission = () => {
    refetchSubscriptionsReq()
    refetchCustomerLogs()
    refetchSubHistory()
    refetchSimServiceStatus()
  }

  const handleWinbackDialogOpen = () => {
    if (faberAdminRole !== 'opsowner') {
      handlePermissionSnackbarOpen()
    } else setWinbackDialog(true)
  }
  const handleWinbackDialogClose = () => {
    setWinbackDialog(false)
    refetchVoucherData()
    refetchWaiverData()
  }
  /* FORMAT ALL SUBSCRIPTION FIELDS */
  useEffect(() => {
    if (getAllSubscriptions) {
      const { data } = getAllSubscriptions.data.body
      const allSubscriptions: SubscriptionsTableRows[] = data.map(
        ({
          firstName,
          lastName,
          user_metadata,
          email,
          customerId,
          userEmail,
          msisdn,
          iccid,
          plan,
          status,
          startAt,
          cycleCount
        }) => ({
          firstName,
          lastName,
          user_metadata,
          email,
          userID: customerId,
          userEmail,
          iccid,
          planStatus: status,
          planName: plan,
          subscriptionStartAt: startAt,
          msisdn,
          cycleCount: String(cycleCount)
        })
      )
      if (allSubscriptions.length > 0) {
        setCustomerData(allSubscriptions[0])
      }
    }
  }, [getAllSubscriptions])

  /* FORMAT SUBSCRIPTION START DATE AND CYCLE COUNT */
  // TO DO: ADD TESTING FOR CHECKING CURRENT BILLING CYCLE DATE AND NEXT BILLING CYCLE DATE
  useEffect(() => {
    const subsDate = new Date(`${customerData.subscriptionStartAt}`)
    const date = new Date(
      subsDate.setMonth(
        subsDate.getMonth() + Number(customerData.cycleCount) - 1
      )
    )

    const nextDate = new Date(date.setMonth(date.getMonth() + 1))
    setCurrentBillingDate(subsDate.toLocaleDateString('en-GB'))
    setNextBillingDate(nextDate.toLocaleDateString('en-GB'))
  }, [customerData])

  const getBillingDate = (key: string) => {
    if (getSubHistory && getSubHistory.length > 0) {
      const unixTimestamp = getSubHistory[0].dates[key].unixSeconds
      const sgTime = new Date(unixTimestamp * 1000).toLocaleString('en-US', {
        timeZone: 'Asia/Singapore'
      })

      const formattedSgTime = format(new Date(sgTime), 'dd MMM yyyy ')

      return formattedSgTime
    } else return ''
  }

  const allFields = useMemo(() => {
    return upgradeNowFlag
      ? [
        {
          mainFieldTitle: 'Profile',
          fields: [
            {
              fieldName: 'First Name',
              fieldValue: customerData.firstName
            },
            {
              fieldName: 'Last Name',
              fieldValue: customerData.lastName
            },
            {
              fieldName: 'Email',
              fieldValue: customerData.userEmail
            }
          ]
        },
        {
          mainFieldTitle: 'Billing Cycle & Account Status',
          fields: [
            {
              fieldName: 'Subscription Start At',
              fieldValue: getBillingDate('subscriptionStartAt')
            },
            {
              fieldName: 'Last Billing Date',
              fieldValue: getBillingDate('lastBillingDate')
            },
            {
              fieldName: 'Next Billing Date',
              fieldValue: getBillingDate('nextBillingDate')
            }
          ]
        }
      ]
      : [
        {
          mainFieldTitle: 'Profile',
          fields: [
            {
              fieldName: 'First Name',
              fieldValue: customerData.firstName
            },
            {
              fieldName: 'Last Name',
              fieldValue: customerData.lastName
            },
            {
              fieldName: 'Email',
              fieldValue: customerData.userEmail
            }
          ]
        },
        {
          mainFieldTitle: 'Billing Cycle & Account Status',
          fields: [
            {
              fieldName: 'Subscription Start At',
              fieldValue: getBillingDate('subscriptionStartAt')
            },
            {
              fieldName: 'Last Billing Date',
              fieldValue: getBillingDate('lastBillingDate')
            },
            {
              fieldName: 'Next Billing Date',
              fieldValue: getBillingDate('nextBillingDate')
            }
          ]
        },
        {
          mainFieldTitle: 'Plan',
          fields: [
            {
              fieldName: 'Next Cycle Plan',
              fieldValue: customerData.planName
            },
            {
              fieldName: '',
              fieldValue: ''
            },
            // empty field is necessary for formatting
            {
              fieldName: '',
              fieldValue: ''
            }
          ]
        }
      ]
  }, [customerData, getSubHistory, upgradeNowFlag])

  /* SET STATE FOR DATA LOGS*/
  useEffect(() => {
    if (dataLog && dataLog.data?.length > 0) {
      setRows(dataLog.data)
    } else {
      setRows([])
    }
  }, [dataLog])

  useEffect(() => {
  if (voucherRows && waiverRows) {
    const combinedRows = [
      ...(Array.isArray(voucherRows) ? voucherRows : []),
      ...(Array.isArray(waiverRows) ? waiverRows : []),
    ];
    setWinbackRows(combinedRows);
  }
}, [voucherRows, waiverRows]);
  useEffect(() => {
    refetchVoucherData()
    refetchWaiverData()
  }, [customerRow])

  return (
    <>
      {gettingAllSubscriptions || getSubHistoryLoading ? (
        <CircularProgress />
      ) : (
        <>
          <Stack>
            <Stack
              flexDirection={'row'}
              sx={{ justifyContent: 'space-evenly' }}
            >
              {isDataRemainingLoading ? (
                <CircularProgress />
              ) : (
                dataRemainingCardFlag && (
                  <DataRemainingCard dataRemaining={dataRemaining} />
                )
              )}

              {isSimServiceStatusLoading ? (
                <CircularProgress />
              ) : (
                simServiceStatus &&
                getSubHistory && (
                  <SubStatusCard
                    getSubHistory={getSubHistory}
                    simServiceStatus={simServiceStatus}
                    onSuccessfulFormSubmission={onSuccessfulFormSubmission}
                  />
                )
              )}
              {isEsimStatusLoading || esimStatus === '' ? (
                <CircularProgress />
              ) : (
                <EsimStatusCard
                  esimStatus={esimStatus}
                  tooltipInfo={tooltipInfo}
                  esimTextColor={esimTextColor}
                />
              )}
              {/* {isImsiLoading || currentImsi === '' || expectedImsi === '' ? (
                <CircularProgress />
              ) : (
                <ImsiCard
                  imsiTextColor={imsiTextColor}
                  currentImsi={currentImsi}
                  expectedImsi={expectedImsi}
                />
              )} */}
            </Stack>
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            {/* ACTION DIALOGS */}

            <FaberFeatureToggle
              feature='split-esim-actions'
              activeComponent={
                <EsimRequestV2Dialog
                  handleRequestEsimDialogClose={handleEsimRequestDialogClose}
                  requestEsimDialog={esimDialogOpen}
                  customerRow={customerData}
                  faberToken={faberToken}
                  updateUserDetails={onSuccessfulFormSubmission}
                  isExternal={false}
                />
              }
              inactiveComponent={
                <EsimRequestDialog
                  handleRequestEsimDialogClose={handleEsimRequestDialogClose}
                  requestEsimDialog={esimDialogOpen}
                  customerRow={customerData}
                  faberToken={faberToken}
                  actionEvent={actionEvent}
                />
              }
            />
            <RefreshNetworkDialog
              faberToken={faberToken}
              handleRefreshNetworkDialogClose={handleRefreshNetworkDialogClose}
              refreshNetworkDialogOpen={refreshNetworkDialogOpen}
              customerRow={customerData}
              isExternal={false} // internal ops user, not external user
            />
            <WinbackDialog
              handleWinbackDialogClose={handleWinbackDialogClose}
              handleWinbackDialogOpen={handleWinbackDialogOpen}
              winbackDialog={winbackDialog}
              customerRow={customerRow}
            />
            <SuspendOrUnsuspendDialog
              onSuccessfulFormSubmission={onSuccessfulFormSubmission}
              faberToken={faberToken}
              handleSuspendOrUnsuspendDialogClose={
                handleSuspendOrUnsuspendDialogClose
              }
              suspendOrUnsuspendDialogOpen={suspendOrUnsuspendDialogOpen}
              customerRow={customerData}
            />
            <NoPermissionSnackbar
              handlePermissionSnackbarClose={handlePermissionSnackbarClose}
              permissionSnackbarOpen={permissionSnackbarOpen}
            />
            <CancelSubV2Dialog
              faberToken={faberToken}
              customerId={id ? id : ''}
              handleCancelDialogClose={handleCancelSubDialogClose}
              cancelDialogOpen={cancelSubDialogOpen}
              onSuccessfulFormSubmission={onSuccessfulFormSubmission}
              />
            <EsimAllocateByWhitelistIDDialog
              isEsimAllocateByWhitelistIDDialogOpen={
                isEsimAllocateByWhitelistIDDialogOpen
              }
              handleCancelEsimAllocateByWhitelistIDDialog={() =>
                setIsEsimAllocateByWhitelistIDDialogOpen(false)
              }
              faberToken={faberToken}
              customerRow={customerRow}
            />
          </Box>
          <ColorBox>
            <Stack
              flexDirection={'row'}
              sx={{ justifyContent: 'space-between', mb: 2 }}
            >
              <TextColorFontHeader>
                Customer ID: {customerRow.userID}
              </TextColorFontHeader>
              <ActionMenuForOneUser
                handleEsimRequestDialogOpen={handleEsimRequestDialogOpen}
                handleEsimRequestDialogV2Open={handleEsimRequestDialogV2Open}
                handleSuspendOrUnsuspendDialogOpen={
                  handleSuspendOrUnsuspendDialogOpen
                }
                handleUserPlanDialogOpen={() => void 0}
                handleUserEditDetailsDialogOpen={() => void 0}
                handleRefreshNetworkDialogOpen={handleRefreshNetworkDialogOpen}
                handleWinbackDialogOpen={handleWinbackDialogOpen}
                handleCancelSubDialogOpen={handleCancelSubDialogOpen}
                showRefreshNetworkButton={showRefreshNetworkButton}
                userId={customerRow.userID}
                handleSwitchWhitelistDialogOpen={() => void 0}
                handleDeletePaymentMethodDialogOpen={() => void 0}
                  handleAssignVoucherDialogOpen={() => void 0}
                  EsimAllocateByWhitelistIDDialogOpen={() =>
                  setIsEsimAllocateByWhitelistIDDialogOpen(true)
                }
              />
            </Stack>
            <Box sx={{ p: 2 }}>
              {allFields.map((field, index) => (
                <React.Fragment key={index + field.mainFieldTitle}>
                  <ProfileListInfo
                    title={field.mainFieldTitle}
                    fields={field.fields}
                  >
                    <UpdateFaberUserDetailsDialog
                      customerRow={customerData}
                      fieldTitle={field.mainFieldTitle}
                      nextPlan={nextPlan}
                      onSuccessfulFormSubmission={onSuccessfulFormSubmission}
                      currentBillingDate={currentBillingDate}
                      nextBillingDate={nextBillingDate}
                    />
                  </ProfileListInfo>
                  {index === 0 && (
                    <ProfileListInfo
                      proportion={4}
                      title=''
                      fields={[
                        {
                          fieldName: 'ICCID',
                          fieldValue: customerData.iccid
                        },
                        {
                          fieldName: 'MSISDN',
                          fieldValue: customerData.msisdn
                        },
                        {
                          fieldName: 'Cycle Count',
                          fieldValue: String(customerData.cycleCount)
                        }
                      ]}
                    />
                  )}
                  {index === 1 && (
                    <ProfileListInfo
                      proportion={4}
                      title=''
                      fields={[
                        {
                          fieldName: 'Account Status',
                          fieldValue: mapStatesToFrontendDropdown(
                            customerData.planStatus
                          )
                        }
                      ]}
                    />
                  )}
                  {index !== allFields.length - 1 && (
                    <Divider sx={{ width: '100%' }} />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </ColorBox>
          {/* User Sub */}
          {subHistoryFlag && (
            <ColorBox sx={{ mt: 2 }}>
              <Stack
                flexDirection={'row'}
                sx={{ justifyContent: 'space-between', mb: 4 }}
              >
                <TextColorFontHeader>
                  Customer&apos;s Subscription History
                </TextColorFontHeader>
                {(customerRow.planStatus === 'ACTIVE' ||
                  customerRow.planStatus === 'EXPIRING') &&
                  getSubHistory &&
                  upgradeNowFlag && (
                    <EditSubscriptionActionMenu
                      refetchSubHistory={refetchSubHistory}
                      getSubHistory={getSubHistory}
                      onSuccessfulFormSubmission={onSuccessfulFormSubmission}
                    />
                  )}
              </Stack>
              <SubHistory
                getSubHistory={getSubHistory}
                getSubHistoryLoading={getSubHistoryLoading}
                refetchSubHistory={refetchSubHistory}
              />
            </ColorBox>
          )}
          {/* CDR LOGS */}
          <ColorBox sx={{ mt: 2 }}>
            <Accordion
              elevation={0}
              defaultExpanded
              expanded={expanded === 'panel1'}
              onChange={handleChange('panel1')}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='panel1a-content'
                id='panel1a-header'
              >
                <TextColorFontHeader>CDR Logs</TextColorFontHeader>
              </AccordionSummary>
              <AccordionDetails>
                <CDRLogsV2
                  faberToken={faberToken}
                  customerRow={customerData}
                  expanded={expanded}
                  isExternal={false}
                />
              </AccordionDetails>
            </Accordion>
          </ColorBox>
          {/* ACCOUNT AND PLAN STATUS UPDATE LOGS */}
          <ColorBox sx={{ mt: 2 }}>
            <Accordion
              elevation={0}
              defaultExpanded={false}
              expanded={expanded === 'panel2'}
              onChange={handleChange('panel2')}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='panel1a-content'
                id='panel1a-header'
              >
                <TextColorFontHeader>
                  Account and Plan Status Update Logs
                </TextColorFontHeader>
              </AccordionSummary>
              <AccordionDetails>
                <AbstractAuditLogDataGrid
                  loading={isStatusUpdateLogsLoading}
                  getRowId={(row) => rows.indexOf(row)}
                  rows={rows}
                  columns={acctStatusColumns}
                  pagination
                  page={page}
                  onPageChange={(newPage) => setPage(newPage)}
                  pageSize={5}
                  tableHeight='350px'
                  rowHeight={50}
                />
              </AccordionDetails>
            </Accordion>
          </ColorBox>
          {/* ESIM REPLACEMENT LOGS */}
          <ColorBox sx={{ mt: 2 }}>
            <Accordion
              elevation={0}
              defaultExpanded={false}
              expanded={expanded === 'panel3'}
              onChange={handleChange('panel3')}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='panel1a-content'
                id='panel1a-header'
              >
                <TextColorFontHeader>eSIM Replacement Logs</TextColorFontHeader>
              </AccordionSummary>
              <AccordionDetails>
                <NewEsimReplacement
                  customerRow={customerData}
                  faberToken={faberToken}
                  esimDialogOpen={esimDialogOpen}
                  expanded={expanded}
                  isExternal={false}
                />
              </AccordionDetails>
            </Accordion>
          </ColorBox>
          <ColorBox sx={{ mt: 2 }}>
            <Accordion
              elevation={0}
              defaultExpanded={false}
              expanded={expanded === 'panel4'}
              onChange={handleChange('panel4')}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='panel1a-content'
                id='panel1a-header'
              >
                <TextColorFontHeader>Winback Logs</TextColorFontHeader>
              </AccordionSummary>
              <AccordionDetails>
                <MUIDataGrid
                  loading={
                    isVoucherWaiverLogsLoading || isBillWaiverLogsLoading
                  }
                  getRowId={(row) => `winback-${winbackRows.indexOf(row)}`}
                  columns={winbackColumns}
                  rows={filterActive ? filteredRows : winbackRows}
                  tableHeight='350px'
                  rowHeight={40}
                  hideFooter
                  disableColumnFilter
                  disableColumnMenu
                  disableSelectionOnClick
                  pagination
                  onPageChange={(newPage) => setPage(newPage)}
                  components={{
                    Toolbar: CustomWinbackToolbar
                  }}
                  componentsProps={{
                    toolbar: {
                      filterProps: {
                        filterFields: filterFields,
                        filterActive,
                        setFilterActive,
                        rows: winbackRows,
                        setFilteredRows
                      }
                    }
                  }}
                  sx={{
                    '& .MuiDataGrid-iconSeparator': {
                      visibility: 'hidden'
                    },
                    '& .MuiDataGrid-footerContainer': {
                      border: '0'
                    },
                    '& .MuiDataGrid-cellContent': {
                      fontSize: '14px'
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontSize: '14px',
                      fontWeight: '500'
                    },
                    '& .MuiDataGrid-cell': {
                      border: '0px'
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      border: '1',
                      borderColor: 'black'
                    },
                    '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
                      outline: 'none'
                    },
                    mx: '-10px'
                  }}
                />
              </AccordionDetails>
            </Accordion>
          </ColorBox>
          <ColorBox sx={{ mt: 2 }}>
            <Accordion
              elevation={0}
              defaultExpanded={false}
              expanded={expanded === 'panel5'}
              onChange={handleChange('panel5')}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='panel1a-content'
                id='panel1a-header'
              >
                <TextColorFontHeader>
                  All Customer Email Logs
                </TextColorFontHeader>
              </AccordionSummary>
              <AccordionDetails>
                <EmailSentLogsV2
                  customerRow={customerData}
                  expanded={expanded}
                  isExternal={false}
                />
              </AccordionDetails>
            </Accordion>
          </ColorBox>
          <ColorBox sx={{ mt: 2 }}>
            <Accordion
              elevation={0}
              defaultExpanded={false}
              expanded={expanded === 'panel6'}
              onChange={handleChange('panel6')}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='panel1a-content'
                id='panel1a-header'
              >
                <TextColorFontHeader>Smart Delay Forms</TextColorFontHeader>
              </AccordionSummary>
              <AccordionDetails>
                <SmartDelayFormLogsV2
                  faberToken={faberToken}
                  customerRow={customerRow}
                  expanded={expanded}
                  isExternal={false}
                />
              </AccordionDetails>
            </Accordion>
          </ColorBox>
        </>
      )}
    </>
  )
}

export default UserProfileV2

interface UserProfileProps {
  customerRow: SubscriptionsTableRows
}
